/* eslint-disable react/jsx-props-no-spreading */
import { faCloudCheck, faUpload } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, FormHelperText, LinearProgress, Stack, Typography } from '@mui/material';
import get from 'lodash-es/get';
import React, { ReactNode } from 'react';
import { DropEvent, useDropzone } from 'react-dropzone';
import { Controller, useFormContext } from 'react-hook-form';

export interface UploadProps {
  rules?: any;
  loading?: boolean;
  accept: string;
  label?: string | ReactNode;
  isReplace?: boolean;
  replaceLabel?: string | ReactNode;
  name: string;
  onChange: <T extends File>(files: T[], event: DropEvent) => void;
  disabled?: boolean;
  multiple?: boolean;
  variant?: any;
  sx?: any;
}

function Upload({
  accept,
  label = 'Drag/Drop Here Or Click To Upload',
  isReplace,
  replaceLabel = 'Drag/Drop Here Or Click To Replace',
  loading = false,
  name,
  rules,
  onChange,
  disabled = false,
  multiple = false,
  variant = 'text',
  sx,
}: UploadProps) {
  const form = useFormContext();
  const { control, clearErrors, setError } = form;

  const onDropRejected = (rejectedFiles) => {
    if (clearErrors && setError) {
      clearErrors();
      if (!multiple && rejectedFiles?.length > 1) {
        setError(name, { type: 'custom', message: 'Please upload only one file.' });
      } else {
        setError(name, { type: 'custom', message: `Please upload files with extensions: ${accept}` });
      }
    }
  };

  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    // Just allow any mime-type and use the extensions.
    accept: accept && { 'application/octet-stream': accept?.split(',').map((item) => item.trim()) },
    disabled,
    multiple,
    onDropAccepted: onChange,
    onDropRejected,
  });

  const files = acceptedFiles.map((file: any) => (
    <Stack key={file.path} direction="row" spacing={1} alignContent="center" justifyContent="center">
      <FontAwesomeIcon icon={faCloudCheck} />
      <span>
        {file.path} - {file.size} bytes
      </span>
    </Stack>
  ));

  const hasUploaded = isReplace || (files && files.length > 0);

  const jsx = (
    <Button
      sx={{
        border: '2px dotted #999',
        ...sx,
      }}
      variant={variant}
      {...getRootProps()}
    >
      <Stack alignItems="center" justifyContent="center">
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" justifyItems="center">
            <Stack>
              <FontAwesomeIcon icon={faUpload} color="rgb(153, 153, 153)" />
            </Stack>
            <Typography color="rgb(153, 153, 153)">
              <label htmlFor={name}>{hasUploaded ? replaceLabel : label}</label>
            </Typography>
          </Stack>
          {hasUploaded && (
            <Typography color="rgb(153, 153, 153)">
              <Stack>{files}</Stack>
            </Typography>
          )}
        </Stack>
      </Stack>
      <input {...getInputProps()} id={name} name={name} type="file" disabled={disabled} hidden />
    </Button>
  );

  if (!control) {
    return jsx;
  }

  const error = get(form, `formState.errors.${name}.message`);

  return (
    <Controller
      name={name}
      rules={rules}
      control={control}
      render={() => (
        <Stack>
          {jsx}
          {error && (
            <FormHelperText error={!!error} sx={{ textAlign: 'center', height: 23.91 }}>
              {error ? error.toString() : ' '}
              <LinearProgress sx={{ opacity: loading ? 1 : 0 }} />
            </FormHelperText>
          )}
        </Stack>
      )}
    />
  );
}

export default Upload;
